import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import { sendReport } from "../../utils/api";
import { Banner, Icon } from "./ui";

/**
 * «Сообщить о проблеме». The text (and an optional screenshot) goes to the
 * team's Telegram review chat; the server adds who the user is and what they
 * have access to, and this sheet adds the device and screen — so nobody has to
 * ask "which phone? which lesson?" before they can help.
 */
const CATEGORIES = ["bug", "unclear", "payment", "other"] as const;
type Category = typeof CATEGORIES[number];
const ICON: Record<Category, string> = { bug: "alert", unclear: "info", payment: "wallet", other: "message" };
const MAX_PHOTO = 8 * 1024 * 1024;

function context(screen: string, lang: string) {
  const inTelegram = !!WebApp.initData;
  return {
    screen,
    platform: inTelegram ? "telegram" : "web",
    device: inTelegram ? WebApp.platform : (navigator.platform || undefined),
    language: lang.slice(0, 5),
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    user_agent: navigator.userAgent.slice(0, 300),
  };
}

export default function ReportSheet({
  token, screen, onClose, initialCategory, initialText,
}: {
  token: string; screen: string; onClose: () => void;
  /** Pre-filled request, e.g. «пришлите ссылку на оплату» from the payment page. */
  initialCategory?: Category; initialText?: string;
}) {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState<Category>(initialCategory ?? "unclear");
  const [text, setText] = useState(initialText ?? "");
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { areaRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_PHOTO) { setError(t("report.photoTooLarge")); return; }
    const r = new FileReader();
    r.onload = () => setPhoto(r.result as string);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (text.trim().length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await sendReport({ category, message: text.trim(), photo_base64: photo ?? undefined, context: context(screen, i18n.language || "ru") }, token);
      setDone(true);
    } catch (e) {
      const m = e instanceof Error ? e.message : "";
      setError(/REPORT_TOO_MANY|429/.test(m) ? t("report.tooMany") : /TOO_LARGE|413/.test(m) ? t("report.photoTooLarge") : t("report.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-backdrop" role="presentation" onClick={() => !busy && onClose()}>
      <div className="sheet glass glass-lg" role="dialog" aria-modal="true" aria-labelledby="report-title" onClick={(e) => e.stopPropagation()}>
        <div className="between" style={{ marginBottom: 12 }}>
          <p className="eyebrow" style={{ margin: 0 }}>{t("report.eyebrow")}</p>
          <button className="icon-btn" onClick={onClose} disabled={busy} aria-label={t("report.close")} style={{ width: 36, height: 36 }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        {done ? (
          <div className="checkout-done">
            <span className="checkout-done__icon"><Icon name="checkCircle" size={30} /></span>
            <h2 className="t-title" id="report-title">{t("report.doneTitle")}</h2>
            <p className="t-sm muted" style={{ margin: 0 }}>{t("report.doneText")}</p>
            <button className="btn btn-primary btn-block" onClick={onClose} style={{ marginTop: 6 }}>{t("report.doneButton")}</button>
          </div>
        ) : (
          <>
            <h2 className="t-title" id="report-title" style={{ margin: "0 0 4px" }}>{t("report.title")}</h2>
            <p className="t-sm muted" style={{ margin: "0 0 14px", lineHeight: 1.45 }}>{t("report.intro")}</p>

            <div className="report-cats" role="radiogroup" aria-label={t("report.categoryLabel")}>
              {CATEGORIES.map((c) => (
                <button key={c} type="button" role="radio" aria-checked={category === c}
                  className={"report-cat" + (category === c ? " is-on" : "")} onClick={() => setCategory(c)}>
                  <Icon name={ICON[c]} size={16} /> {t(`report.cat_${c}`)}
                </button>
              ))}
            </div>

            <label style={{ display: "block", marginTop: 14 }}>
              <span className="t-xs muted-2" style={{ fontWeight: 700 }}>{t("report.messageLabel")}</span>
              <textarea ref={areaRef} className="textarea report-text" value={text} maxLength={2000} rows={5}
                onChange={(e) => setText(e.target.value)} placeholder={t(`report.placeholder_${category}`)} />
            </label>

            <input ref={fileRef} type="file" accept="image/*" onChange={pick} style={{ display: "none" }} />
            {photo ? (
              <div className="report-photo">
                <img src={photo} alt={t("report.screenshot")} />
                <button className="icon-btn" onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ""; }}
                  aria-label={t("report.removeScreenshot")} style={{ width: 30, height: 30 }}>
                  <Icon name="x" size={15} />
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-ghost" onClick={() => fileRef.current?.click()} style={{ marginTop: 8, paddingInline: 10, minHeight: 36 }}>
                <Icon name="image" size={16} /> {t("report.addScreenshot")}
              </button>
            )}

            <p className="t-xs muted-2" style={{ margin: "10px 0 0", lineHeight: 1.45 }}>{t("report.privacy")}</p>
            {error && <div style={{ marginTop: 10 }}><Banner kind="error">{error}</Banner></div>}

            <button className="btn btn-primary btn-block" onClick={submit} disabled={busy || text.trim().length < 3} style={{ marginTop: 14 }}>
              {busy ? <span className="spinner" /> : <><Icon name="send" size={17} /> {t("report.send")}</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
